import { getContrastRatio } from "@mui/material/styles";

/**
 * Classic Amber brand hue used as tenant `theme_color` fallback / browser chrome.
 * Distinct from the accessible primary-action fill (`tokens.cta.fill`).
 *
 * This module owns Theme Color chrome admission: valid `#RRGGBB` with
 * 4.5:1 vs white, else {@link PLATFORM_PRIMARY}. Filled CTA identity and
 * branding-white foreground live in `resolveTenantActionTokens`.
 * Callers must not invent a second contrast algorithm.
 */
export const PLATFORM_PRIMARY = "#E8750C";

const HEX_RRGGBB = /^#[0-9A-Fa-f]{6}$/;
const WHITE = "#FFFFFF";
const MIN_CONTRAST_WITH_WHITE = 4.5;

/**
 * True only for trimmed `#RRGGBB`. Short hex, named colors, and empty values fail.
 * Never throws.
 */
export function isValidHexColor(value?: string | null): boolean {
  if (typeof value !== "string") return false;
  return HEX_RRGGBB.test(value.trim());
}

/**
 * Valid `#RRGGBB` (trimmed) or the platform primary. No contrast check.
 * Never throws. Prefer {@link resolveAccessiblePrimary} for UI overlays.
 */
export function resolveTenantPrimary(themeColor?: string | null): string {
  if (!isValidHexColor(themeColor)) return PLATFORM_PRIMARY;
  return themeColor!.trim();
}

/**
 * Authoritative accessible fill for a candidate hex (theme chrome fallback).
 * CTA token assembly is owned by `resolveTenantBranding`.
 *
 * Accepts trimmed `#RRGGBB` whose contrast against white is at least 4.5:1.
 * Empty, invalid, or low-contrast values return {@link PLATFORM_PRIMARY}.
 * Light-only. Never throws. Does not mutate MUI.
 */
export function resolveAccessiblePrimary(themeColor?: string | null): string {
  try {
    if (!isValidHexColor(themeColor)) return PLATFORM_PRIMARY;
    const hex = themeColor!.trim();
    if (getContrastRatio(hex, WHITE) < MIN_CONTRAST_WITH_WHITE) {
      return PLATFORM_PRIMARY;
    }
    return hex;
  } catch {
    return PLATFORM_PRIMARY;
  }
}
